import { useMemo, useState } from "react";
import {
  relievingPressureKpaAbs,
  sizeGasOrVapor,
  type GasSizingInput,
  type ValveType,
} from "../calc/gas";
import { selectOrificeWithParallel } from "../calc/orifice";
import { Field, Item, VALVE_LABELS, num } from "./formBits";

/** 화면 입력은 문자열로 보관하고, 계산 직전에만 숫자로 바꾼다. */
interface FormState {
  flow: string;
  temperature: string;
  compressibility: string;
  molecularWeight: string;
  specificHeatRatio: string;
  setPressure: string;
  overpressure: string;
  backpressureGauge: string;
  valveType: ValveType;
  kb: string;
  kc: string;
}

/** 초기값은 API 520 Part I 5.6.3.2 Example 1 조건이다. 결과는 P 오리피스. */
const INITIAL: FormState = {
  flow: "24270",
  temperature: "348",
  compressibility: "0.9",
  molecularWeight: "51",
  specificHeatRatio: "1.11",
  setPressure: "517",
  overpressure: "10",
  backpressureGauge: "0",
  valveType: "conventional",
  kb: "",
  kc: "1.0",
};

export function GasSizingPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const p1 = useMemo(
    () => relievingPressureKpaAbs(num(f.setPressure), num(f.overpressure)),
    [f.setPressure, f.overpressure],
  );

  const sizing = useMemo(() => {
    const kb = f.kb.trim() === "" ? undefined : num(f.kb);
    const input: GasSizingInput = {
      requiredFlowKgH: num(f.flow),
      relievingTemperatureK: num(f.temperature),
      compressibility: num(f.compressibility),
      molecularWeight: num(f.molecularWeight),
      specificHeatRatio: num(f.specificHeatRatio),
      relievingPressureKpaAbs: p1,
      backpressureKpaAbs: num(f.backpressureGauge) + 101.325,
      valveType: f.valveType,
      combinationCorrection: num(f.kc),
      ...(kb === undefined ? {} : { bellowsBackpressureCorrection: kb }),
    };
    return sizeGasOrVapor(input);
  }, [f, p1]);

  const selection = useMemo(
    () => (sizing.ok ? selectOrificeWithParallel(sizing.requiredAreaMm2) : null),
    [sizing],
  );

  return (
    <section className="card">
      <h2>기체·증기 사이징</h2>
      <p className="hint">
        API 520 Part I 10판 5.6절. 배압에 따라 임계/아임계 유동을 자동 판별합니다.
        초기값은 표준서 5.6.3.2 예제 조건입니다.
      </p>

      <div className="grid-form">
        <Field label="소요 유량 W" unit="kg/h" help="flowMass">
          <input value={f.flow} onChange={(e) => set("flow")(e.target.value)} />
        </Field>
        <Field label="배출 온도 T" unit="K" help="temperatureK">
          <input
            value={f.temperature}
            onChange={(e) => set("temperature")(e.target.value)}
          />
        </Field>
        <Field label="분자량 M" unit="kg/kmol" help="molecularWeight">
          <input
            value={f.molecularWeight}
            onChange={(e) => set("molecularWeight")(e.target.value)}
          />
        </Field>
        <Field label="비열비 k" unit="Cp/Cv" help="specificHeatRatio">
          <input
            value={f.specificHeatRatio}
            onChange={(e) => set("specificHeatRatio")(e.target.value)}
          />
        </Field>
        <Field label="압축계수 Z" unit="—" help="compressibility">
          <input
            value={f.compressibility}
            onChange={(e) => set("compressibility")(e.target.value)}
          />
        </Field>
        <Field label="설정압" unit="kPag" help="setPressure">
          <input
            value={f.setPressure}
            onChange={(e) => set("setPressure")(e.target.value)}
          />
        </Field>
        <Field label="허용 과압" unit="%" help="overpressure">
          <input
            value={f.overpressure}
            onChange={(e) => set("overpressure")(e.target.value)}
          />
        </Field>
        <Field label="총 배압" unit="kPag" help="backpressureGauge">
          <input
            value={f.backpressureGauge}
            onChange={(e) => set("backpressureGauge")(e.target.value)}
          />
        </Field>
        <Field label="밸브 형식" unit="" help="valveType">
          <select
            value={f.valveType}
            onChange={(e) => set("valveType")(e.target.value as ValveType)}
          >
            {(Object.keys(VALVE_LABELS) as ValveType[]).map((t) => (
              <option key={t} value={t}>
                {VALVE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="배압 보정 Kb"
          unit={f.valveType === "balanced-bellows" ? "제작사 자료" : "해당 없음"}
          help="kbBellows"
        >
          <input
            value={f.kb}
            placeholder={
              f.valveType === "balanced-bellows" ? "예: 0.9" : "1.0 고정"
            }
            disabled={f.valveType !== "balanced-bellows"}
            onChange={(e) => set("kb")(e.target.value)}
          />
        </Field>
        <Field label="파열판 조합 Kc" unit="없으면 1.0" help="kc">
          <input value={f.kc} onChange={(e) => set("kc")(e.target.value)} />
        </Field>
      </div>

      {!sizing.ok ? (
        <div className="result result--error" role="alert">
          {sizing.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">소요 면적</span>
            <strong className="result__value">
              {sizing.requiredAreaMm2.toLocaleString("ko-KR", {
                maximumFractionDigits: 0,
              })}
            </strong>
            <span className="result__label">mm²</span>
            <span className="badge">
              {sizing.flowRegime === "critical" ? "임계유동" : "아임계유동"}
            </span>
          </div>

          {selection?.ok && (
            <div className="result__headline" style={{ marginTop: 10 }}>
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {selection.orifice.designation}
              </strong>
              {selection.valveCount > 1 && (
                <span className="badge">{selection.valveCount}개 병렬</span>
              )}
              <span className="muted">
                여유율 {selection.marginPct.toFixed(1)} %
              </span>
            </div>
          )}
          {selection && !selection.ok && (
            <p className="muted" style={{ marginTop: 10 }}>
              {selection.message}
            </p>
          )}

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="적용 식" value={sizing.equation} />
            <Item
              label="배출 압력 P1"
              value={`${p1.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa abs`}
            />
            <Item
              label="임계유동 압력 Pcf"
              value={`${sizing.intermediates.criticalFlowPressureKpaAbs.toLocaleString(
                "ko-KR",
                { maximumFractionDigits: 1 },
              )} kPa abs`}
            />
            <Item
              label="임계압력비 Pcf/P1"
              value={sizing.intermediates.criticalPressureRatio.toFixed(4)}
            />
            <Item
              label="배압비 r = P2/P1"
              value={sizing.intermediates.backpressureRatio.toFixed(4)}
            />
            {sizing.intermediates.coefficientC !== null && (
              <Item
                label="계수 C (식 13)"
                value={sizing.intermediates.coefficientC.toFixed(5)}
              />
            )}
            {sizing.intermediates.coefficientF2 !== null && (
              <Item
                label="계수 F2 (식 22)"
                value={sizing.intermediates.coefficientF2.toFixed(4)}
              />
            )}
            <Item label="Kd" value={sizing.intermediates.kd.toFixed(3)} />
            <Item label="Kb" value={sizing.intermediates.kb.toFixed(3)} />
            <Item label="Kc" value={sizing.intermediates.kc.toFixed(3)} />
          </dl>

          {sizing.warnings.length > 0 && (
            <ul className="warnings">
              {sizing.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

